import { createClient } from "@supabase/supabase-js";

// Supabase client used everywhere in the CRM.
// Config comes from environment variables (.env.local during dev).
// NEVER commit the keys to version control.

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as
  | string
  | undefined;

if (!supabaseUrl || !supabaseAnonKey) {
  // eslint-disable-next-line no-console
  console.warn(
    "Supabase credentials missing. Copy .env.example to .env.local and fill in VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.",
  );
}

// RESILIENCE Tier 1 — a request TIMEOUT on every Supabase call. Without this, a
// network/backend blip can leave a request "pending" forever (the 2026-06-18
// incident: reports hung blank until a manual reload). With it, a wedged request
// aborts and ERRORS instead, so react-query retries + the ConnectivityBanner can
// detect the blip and auto-recover. Storage uploads get a long fuse so large
// files aren't cut off; everything else times out fast.
const REST_TIMEOUT_MS = 20_000;
const UPLOAD_TIMEOUT_MS = 120_000;
// FN-TIMEOUT-01 (Seth 2026-10-23, the on-call sheet upload): an Edge Function that
// reads a document with a model takes 20–90 s; the 20 s REST fuse was aborting those
// calls mid-flight, which supabase-js reports as "Failed to send a request to the
// Edge Function" and the function logs show as an OPTIONS with no POST. Functions get
// the platform's own ceiling (150 s) plus a margin.
const FUNCTION_TIMEOUT_MS = 170_000;
function timeoutFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
  const ms = url.includes("/storage/v1/") ? UPLOAD_TIMEOUT_MS : url.includes("/functions/v1/") ? FUNCTION_TIMEOUT_MS : REST_TIMEOUT_MS;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(new DOMException("Request timed out", "TimeoutError")), ms);
  // Honor a caller-supplied signal (e.g. react-query cancellation) too.
  const caller = init?.signal;
  if (caller) {
    if (caller.aborted) ctrl.abort(caller.reason);
    else caller.addEventListener("abort", () => ctrl.abort(caller.reason), { once: true });
  }
  return fetch(input, { ...init, signal: ctrl.signal }).finally(() => clearTimeout(timer));
}

export const supabase = createClient(
  supabaseUrl ?? "https://placeholder.supabase.co",
  supabaseAnonKey ?? "placeholder-anon-key",
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
    global: { fetch: timeoutFetch },
  },
);
